import { Box, Grid, Typography, styled } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import ChartCard from 'components/charts/chart-card';
import ChartEmptyState from 'components/charts/chart-empty-state';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import CategoryBarChart from 'views/playground/features/data-lab/components/charts/category-bar-chart';
import CorrelationScatterChart from 'views/playground/features/data-lab/components/charts/correlation-scatter-chart';
import DistributionDonutChart from 'views/playground/features/data-lab/components/charts/distribution-donut-chart';
import TrendLineChart from 'views/playground/features/data-lab/components/charts/trend-line-chart';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import FilterChip from 'views/playground/features/data-lab/components/filter-chip';
import KpiCard from 'views/playground/features/data-lab/components/kpi-card';
import SiteFilterList from 'views/playground/features/data-lab/components/site-filter-list';
import { LAB_FIXTURE } from 'views/playground/features/data-lab/constants/lab-fixtures';
import {
  deriveCategoryBarData,
  deriveDistributionDonutData,
  deriveOverviewCorrelationData,
  deriveOverviewKpis,
  deriveOverviewTrendData,
} from 'views/playground/features/data-lab/utils/derive-chart-data';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

// Derived once at module scope: neither depends on the cross-filter, only the
// fixture. The donut and the bar chart are the Overview's two selectors (spec
// 9.1), so both always show every site; only the "downstream" charts below
// filter their own data by `selectedSite`.
const categoryBarData = deriveCategoryBarData();
const siteFilterOptions = LAB_FIXTURE.sites.map((site) => ({ id: site.id, label: site.name }));

type TrendDelta = { value: string; direction: 'up' | 'down'; sentiment: 'good' | 'bad' | 'neutral' };

// Every KPI trend covers the same 12 most recent weeks, so a single
// first-vs-last comparison drives every delta chip. `goodDirection` is the only
// thing that varies per metric: growing biomass is good, a growing feed
// conversion ratio, mortality rate, or feed cost is not.
const trendDelta = (trend: number[], formatter: (value: number) => string, goodDirection: 'up' | 'down'): TrendDelta | undefined => {
  if (trend.length < 2) return undefined;

  const change = trend[trend.length - 1] - trend[0];
  const direction: 'up' | 'down' = change >= 0 ? 'up' : 'down';
  const sentiment: TrendDelta['sentiment'] = change === 0 ? 'neutral' : direction === goodDirection ? 'good' : 'bad';

  return { value: formatter(Math.abs(change)), direction, sentiment };
};

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
}));

const PageTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

// Selecting a donut slice or bar sets
// `selectedSite`, which every other chart on the page and all four KPI values
// read back through. Re-clicking the selected element, the filter chip's clear
// button, and Escape all clear it identically. `SiteFilterList` is a parallel,
// keyboard-operable path to the same state: the donut and bar stay
// mouse-driven exactly as before, but a keyboard-only user is no longer locked
// out of the page's primary interaction.
export const OverviewScreenBody: React.FC = () => {
  const { formatCurrencyPerKg, formatKg, formatPercent, formatRatio } = useMetricFormatters();
  const chartPalette = useChartPalette();

  const [selectedSite, setSelectedSite] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedSite(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const clearFilter = () => setSelectedSite(null);

  const distributionDonutData = useMemo(() => deriveDistributionDonutData('Other'), []);
  const correlationData = useMemo(() => deriveOverviewCorrelationData('Sites'), []);

  const kpis = useMemo(() => deriveOverviewKpis(selectedSite), [selectedSite]);

  // The "Other" bucket has no backing fixture site, so resolve it from the
  // donut categories before falling back to the raw id.
  const selectedSiteName = selectedSite
    ? (LAB_FIXTURE.sites.find((site) => site.id === selectedSite)?.name ??
      distributionDonutData.find((point) => point.id === selectedSite)?.label ??
      selectedSite)
    : null;

  const trendSeriesLabel = selectedSiteName ? `${selectedSiteName} biomass` : 'All sites biomass';
  const trendData = useMemo(() => deriveOverviewTrendData(selectedSite, trendSeriesLabel), [selectedSite, trendSeriesLabel]);

  const scatterData = useMemo(() => {
    if (!selectedSite) return correlationData;
    return correlationData.map((series) => ({
      ...series,
      points: series.points.filter((point) => point.id === selectedSite),
    }));
  }, [selectedSite, correlationData]);

  const emptyKpiValue = '—';
  const kpiValue = (formatted: string) => (kpis.isEmpty ? emptyKpiValue : formatted);

  return (
    <>
      <HeaderRow>
        <PageTitle chartPalette={chartPalette} variant="h2">
          Overview
        </PageTitle>
        {selectedSiteName && <FilterChip label={`Site: ${selectedSiteName}`} onClear={clearFilter} />}
        <SiteFilterList options={siteFilterOptions} selectedId={selectedSite} onSelect={setSelectedSite} />
      </HeaderRow>

      <Box display="flex" flexDirection="column" gap={4}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          }}
          gridAutoRows="1fr"
          gap={4}
        >
          <KpiCard
            label="Biomass"
            value={kpiValue(formatKg(kpis.biomassKg))}
            delta={kpis.isEmpty ? undefined : trendDelta(kpis.biomassTrend, formatKg, 'up')}
            trend={kpis.biomassTrend}
          />
          <KpiCard
            label="Feed conversion ratio"
            value={kpiValue(formatRatio(kpis.feedConversionRatio))}
            delta={kpis.isEmpty ? undefined : trendDelta(kpis.feedConversionTrend, formatRatio, 'down')}
            trend={kpis.feedConversionTrend}
          />
          <KpiCard
            label="Mortality rate"
            value={kpiValue(formatPercent(kpis.mortalityRatePercent))}
            delta={kpis.isEmpty ? undefined : trendDelta(kpis.mortalityTrend, formatPercent, 'down')}
            trend={kpis.mortalityTrend}
          />
          <KpiCard
            label="Feed cost per kg"
            value={kpiValue(formatCurrencyPerKg(kpis.feedCostPerKg))}
            delta={kpis.isEmpty ? undefined : trendDelta(kpis.feedCostTrend, formatCurrencyPerKg, 'down')}
            trend={kpis.feedCostTrend}
          />
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            <ChartCard
              title="Biomass trend"
              subtitle={selectedSiteName ? `Filtered to ${selectedSiteName}` : 'All sites'}
              isEmpty={trendData.series.length === 0}
              emptyState={
                <ChartEmptyState height={320} message="No data for this selection." clearLabel="Clear filter" onClear={clearFilter} />
              }
            >
              <TrendLineChart data={trendData} />
            </ChartCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ChartCard title="Biomass by site" subtitle="Click a slice to filter">
              <DistributionDonutChart data={distributionDonutData} selectedId={selectedSite} onSelect={setSelectedSite} />
            </ChartCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ChartCard title="Biomass and feed by site" subtitle="Click a bar to filter">
              <CategoryBarChart data={categoryBarData} selectedId={selectedSite} onSelect={setSelectedSite} />
            </ChartCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ChartCard
              title="Weight vs feed conversion"
              subtitle="Scatter chart · one point per site"
              isEmpty={scatterData.every((series) => series.points.length === 0)}
              emptyState={
                <ChartEmptyState height={320} message="No data for this selection." clearLabel="Clear filter" onClear={clearFilter} />
              }
            >
              <CorrelationScatterChart data={scatterData} />
            </ChartCard>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const OverviewScreen: React.FC = () => (
  <DataLab>
    <OverviewScreenBody />
  </DataLab>
);

export default OverviewScreen;
